import { User, type UserProfile } from 'oidc-client-ts';

/**
 * Drop-in replacement for the subset of oidc-client-ts's UserManager this app uses, backed by the
 * pme-factory-supervisor session instead of a browser-side OIDC handshake.
 *
 * Why this exists: behind the factory supervisor the app is served from an instance subdomain
 * (comptaai-pr1234.factory.pme.acceo.com). Driving our own handshake from there would send the browser
 * straight to Identity Server with a redirect_uri on that subdomain — which is not registered, and cannot
 * be, since the whole point of the supervisor is that only its own URL is. The supervisor has already
 * completed the handshake server-side; this reads the resulting session.
 *
 * Returns real `User` instances rather than look-alike objects, so `.expired`, `.scopes`, `.profile` and
 * `toStorageString()` behave natively and cannot drift from the library.
 */

/** Shape of GET /_factory/session (ASP.NET Core serialises camelCase). */
interface FactorySessionPayload {
    authenticated: boolean;
    accessToken: string | null;
    idToken: string | null;
    tokenType: string | null;
    expiresAt: number | null;
    scope: string | null;
    profile: { sub: string | null; name: string | null; email: string | null } | null;
}

// The supervisor reserves /_factory/* on every host it serves, including instance subdomains, so these are
// same-origin requests: no CORS, and the domain-scoped session cookie is sent automatically.
const SESSION_ENDPOINT = '/_factory/session';
const LOGIN_ENDPOINT = '/_factory/login';
const LOGOUT_ENDPOINT = '/_factory/logout';

// `state` must survive a full-page round trip through the supervisor. The trip returns to this same origin,
// so localStorage persists across it. Load-bearing for the Acomba Desktop launch, which carries its
// assertion in OIDC state (DesktopAuth.tsx) and reads it back in SigninCallback.tsx.
const PENDING_STATE_KEY = 'factory.pending-signin-state';

/** Decodes a JWT payload without verifying it. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
    const parts = token.split('.');
    if (parts.length < 2 || parts[1] === undefined) return null;
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        // atob yields Latin-1 bytes, so the UTF-8 in a claim must be decoded explicitly: handed straight
        // to JSON.parse, a name of "André Côté" comes out "AndrÃ© CÃ´tÃ©". oidc-client-ts decodes through
        // jwt-decode, which does this — skipping it would make the supervised profile differ from the
        // standalone one for most of the user base.
        const bytes = Uint8Array.from(
            atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')),
            (c) => c.charCodeAt(0),
        );
        return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
    } catch {
        return null;
    }
}

/**
 * Builds the profile the way oidc-client-ts does — from the id_token claims — so consumers see the same
 * claim set they would under a real handshake. Falls back to the claims the supervisor reported explicitly
 * when there is no id_token to decode.
 */
function buildProfile(payload: FactorySessionPayload): UserProfile {
    const decoded = payload.idToken !== null ? decodeJwtPayload(payload.idToken) : null;

    if (decoded !== null && typeof decoded.sub === 'string') {
        return decoded as unknown as UserProfile;
    }

    // No id_token: synthesise the minimum this app reads (sub, name, email). Cast because UserProfile
    // mandates iss/aud/exp/iat, which we have no truthful values for — inventing them would be worse than
    // omitting them, since nothing in this app reads them.
    return {
        sub: payload.profile?.sub ?? '',
        name: payload.profile?.name ?? undefined,
        email: payload.profile?.email ?? undefined,
    } as unknown as UserProfile;
}

/** Builds a real User from a supervisor session, or null when the session is not usable. */
function toUser(payload: FactorySessionPayload, userState: unknown): User | null {
    if (!payload.authenticated || payload.accessToken === null) return null;

    return new User({
        access_token: payload.accessToken,
        id_token: payload.idToken ?? undefined,
        token_type: payload.tokenType ?? 'Bearer',
        scope: payload.scope ?? undefined,
        // Truthful expiry straight from the supervisor. authRefreshScheduler times itself off this, so a
        // synthesised value would desynchronise refresh from the real token lifetime.
        expires_at: payload.expiresAt ?? undefined,
        profile: buildProfile(payload),
        // NOTE: the constructor argument is `userState`, while the resulting property is `state`. Passing
        // `state` here compiles away silently and loses the desktop assertion.
        userState,
    });
}

/**
 * Reads the supervisor session, or null when the request itself could not be completed — an unreachable
 * supervisor, a 502 from its reverse proxy, or a body that is not JSON at all (a stack reached outside the
 * supervisor answers /_factory/session with the SPA's own index.html and a 200).
 *
 * Distinct from "signed out", which the supervisor reports as a 200 with authenticated:false and which also
 * yields null downstream. The two are deliberately NOT distinguished at the boundary: every consumer of
 * oidcUserManager.getUser() is written against the null the real UserManager returns, and only signinSilent's
 * call sites wrap the call at all — so a throw here escalates a transient blip from "re-authenticate" to
 * "every API request fails" (buildTenantHeaders assembles X-Sso-Bearer from it on every request).
 */
async function fetchSession(): Promise<FactorySessionPayload | null> {
    try {
        const response = await fetch(SESSION_ENDPOINT, {
            credentials: 'include',
            headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`status ${response.status}`);
        return await response.json() as FactorySessionPayload;
    } catch (e) {
        console.error('[factory] session request failed:', e);
        return null;
    }
}

function takePendingState(): unknown {
    const raw = window.localStorage.getItem(PENDING_STATE_KEY);
    if (raw === null) return undefined;
    window.localStorage.removeItem(PENDING_STATE_KEY);
    try {
        return JSON.parse(raw);
    } catch {
        return undefined;
    }
}

interface SigninRedirectArgs {
    state?: unknown;
    prompt?: string;
    max_age?: number;
}

/** Performs a full-page navigation. Injectable because jsdom makes window.location.assign read-only. */
export type Navigate = (url: string) => void;

export class SupervisedUserManager {
    private readonly navigate: Navigate;

    constructor(navigate: Navigate = (url) => window.location.assign(url)) {
        this.navigate = navigate;
    }

    /** Current session, or null when not signed in — or when the supervisor could not be reached. */
    async getUser(): Promise<User | null> {
        const session = await fetchSession();
        return session === null ? null : toUser(session, undefined);
    }

    /**
     * The supervisor refreshes tokens server-side, so silent renewal is just re-reading the session.
     * Returning null lets the callers' existing fallbacks escalate to a full redirect, unchanged.
     */
    async signinSilent(): Promise<User | null> {
        const session = await fetchSession();
        return session === null ? null : toUser(session, undefined);
    }

    /**
     * Starts an interactive login, returning to this app's /signin-callback so SigninCallback.tsx runs
     * exactly as it does under a real handshake.
     */
    // Not `async`: there is nothing to await, and the signature only needs to be Promise-returning to match
    // what callers expect from UserManager.
    signinRedirect(args?: SigninRedirectArgs): Promise<void> {
        if (args?.state !== undefined) {
            window.localStorage.setItem(PENDING_STATE_KEY, JSON.stringify(args.state));
        }

        const params = new URLSearchParams({ returnUrl: `${window.location.origin}/signin-callback` });

        // Forwarded, not dropped. Header.tsx asks for prompt=login / max_age=0 to force re-authentication
        // before a sensitive action; swallowing it would downgrade that gate to a no-op that still looks
        // like it worked.
        if (args?.prompt !== undefined) params.set('prompt', args.prompt);
        if (args?.max_age !== undefined) params.set('maxAge', String(args.max_age));

        this.navigate(`${LOGIN_ENDPOINT}?${params.toString()}`);
        return Promise.resolve();
    }

    /**
     * Completes login by reading the session the supervisor established. Throws where getUser returns
     * null: arriving on /signin-callback with no session is a broken login, not a signed-out visitor, and
     * SigninCallback.tsx already renders that. The pending state is left in place when the read failed, so
     * a reload can still recover the desktop assertion.
     */
    async signinCallback(): Promise<User> {
        const session = await fetchSession();
        const user = session === null ? null : toUser(session, takePendingState());
        if (user === null) throw new Error('Factory supervisor reported no session after login.');
        return user;
    }

    /** Signs out of the factory and Identity Server. */
    signoutRedirect(): Promise<void> {
        const returnUrl = `${window.location.origin}/signout-callback`;
        this.navigate(`${LOGOUT_ENDPOINT}?returnUrl=${encodeURIComponent(returnUrl)}`);
        return Promise.resolve();
    }

    /** The supervisor cleared the session before redirecting here, so there is nothing left to do. */
    signoutRedirectCallback(): Promise<void> {
        return Promise.resolve();
    }

    /** No client-side session to clear — the supervisor owns it. */
    removeUser(): Promise<void> {
        window.localStorage.removeItem(PENDING_STATE_KEY);
        return Promise.resolve();
    }
}
